import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { useAuth } from "@/contexts/AuthContext";
import { ROLES, ROLE_BADGE } from "@/lib/perms";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Users, Trash2, KeyRound } from "lucide-react";
import { toast } from "sonner";

const emptyForm = { email: "", name: "", password: "", role: "editor" };

const Team = () => {
  const { t } = useLang();
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [pwTarget, setPwTarget] = useState(null);
  const [newPw, setNewPw] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await api.get("/users");
      setMembers(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat daftar tim");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const err = (e) => toast.error(e?.response?.data?.detail || "Error");

  const createMember = async () => {
    if (!form.email || !form.password) { toast.error(t("email_password_required")); return; }
    try {
      await api.post("/users", form);
      toast.success(t("member_created"));
      setOpen(false);
      setForm(emptyForm);
      await load();
    } catch (e) { err(e); }
  };

  const changeRole = async (m, role) => {
    try {
      await api.put(`/users/${m.user_id}`, { role });
      toast.success(t("member_updated"));
      await load();
    } catch (e) { err(e); }
  };

  const resetPassword = async () => {
    try {
      await api.put(`/users/${pwTarget.user_id}`, { password: newPw });
      toast.success(t("member_updated"));
      setPwTarget(null);
      setNewPw("");
    } catch (e) { err(e); }
  };

  const del = async (m) => {
    if (!window.confirm(`${t("delete_member_confirm")} ${m.email}?`)) return;
    try {
      await api.delete(`/users/${m.user_id}`);
      toast.success(t("member_deleted"));
      await load();
    } catch (e) { err(e); }
  };

  return (
    <div className="space-y-6" data-testid="team-management-view">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("team")}</h2>
          <p className="text-sm text-slate-500 mt-1">{members.length} {t("team_members")}</p>
        </div>
        <Button onClick={() => { setForm(emptyForm); setOpen(true); }} data-testid="add-member-button" className="bg-orange-500 hover:bg-orange-600 gap-2">
          <Plus className="w-4 h-4" /> {t("add_member")}
        </Button>
      </div>

      <Card className="p-4 border-slate-200 bg-slate-50/60 text-xs text-slate-600 space-y-1">
        <div><b className="text-slate-800">{t("admin")}</b> — {t("role_desc_admin")}</div>
        <div><b className="text-slate-800">{t("editor")}</b> — {t("role_desc_editor")}</div>
        <div><b className="text-slate-800">{t("approver")}</b> — {t("role_desc_approver")}</div>
        <div><b className="text-slate-800">{t("viewer")}</b> — {t("role_desc_viewer")}</div>
        <div><b className="text-slate-800">{t("no_access")}</b> — {t("role_desc_no_access")}</div>
      </Card>

      <Card className="border-slate-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                <th className="px-4 py-3">{t("name")}</th>
                <th className="px-4 py-3">{t("email")}</th>
                <th className="px-4 py-3 w-48">{t("role")}</th>
                <th className="px-4 py-3 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => {
                const isSelf = m.user_id === user?.user_id;
                return (
                  <tr key={m.user_id} className="border-b border-slate-100 hover:bg-slate-50/70" data-testid={`member-row-${m.email}`}>
                    <td className="px-4 py-3 font-medium text-slate-900">
                      {m.name} {isSelf && <span className="text-[10px] text-slate-400 ml-1">({t("you")})</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{m.email}</td>
                    <td className="px-4 py-3">
                      {isSelf ? (
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold border ${ROLE_BADGE[m.role]}`}>{t(m.role)}</span>
                      ) : (
                        <Select value={m.role} onValueChange={(v) => changeRole(m, v)}>
                          <SelectTrigger data-testid={`role-select-${m.email}`} className="h-8 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {ROLES.map((r) => <SelectItem key={r} value={r}>{t(r)}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => { setPwTarget(m); setNewPw(""); }}
                        className="p-1.5 text-slate-500 hover:text-slate-900"
                        title={t("reset_password")}
                        data-testid={`reset-password-${m.email}`}
                      ><KeyRound className="w-4 h-4" /></button>
                      {!isSelf && (
                        <button
                          onClick={() => del(m)}
                          className="p-1.5 text-slate-500 hover:text-rose-600 ml-1"
                          data-testid={`delete-member-${m.email}`}
                        ><Trash2 className="w-4 h-4" /></button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {members.length === 0 && (
          <div className="p-12 text-center text-slate-400">
            <Users className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          </div>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t("new_member")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">{t("email")}</Label>
              <Input data-testid="member-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">{t("name")}</Label>
              <Input data-testid="member-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">{t("password")}</Label>
              <Input type="password" data-testid="member-password-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">{t("role")}</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="member-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => <SelectItem key={r} value={r}>{t(r)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button onClick={createMember} data-testid="save-member-button" className="bg-orange-500 hover:bg-orange-600">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pwTarget} onOpenChange={(v) => !v && setPwTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t("reset_password")} — {pwTarget?.email}</DialogTitle></DialogHeader>
          <div>
            <Label className="text-xs">{t("password")}</Label>
            <Input type="password" data-testid="new-password-input" value={newPw} onChange={(e) => setNewPw(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwTarget(null)}>{t("cancel")}</Button>
            <Button onClick={resetPassword} data-testid="save-password-button" className="bg-orange-500 hover:bg-orange-600">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Team;
